// SPDX-License-Identifier: AGPL-3.0-or-later

import * as EmojiPickerCommands from '@app/features/emoji/commands/EmojiPickerCommands';
import EmojiPicker from '@app/features/emoji/state/EmojiPicker';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import Guilds from '@app/features/guild/state/Guilds';
import {CloneEmojiMenuItem} from '@app/features/ui/action_menu/items/CloneEmojiMenuItem';
import styles from '@app/features/ui/action_menu/items/MenuItems.module.css';
import {ReverseImageSearchMenuItems} from '@app/features/ui/action_menu/items/ReverseImageSearchMenuItems';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {useLingui} from '@lingui/react/macro';
import {ArrowSquareOutIcon, ClipboardIcon, LinkIcon, StarIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

interface EmojiContextMenuItemsProps {
	emoji: FlatEmoji;
	onClose: () => void;
}
const useEmojiHandlers = (emoji: FlatEmoji, onClose: () => void) => {
	const {i18n, t} = useLingui();
	const canFavorite = !emoji.id || Boolean(emoji.guildId && Guilds.getGuild(emoji.guildId));
	const isFavorite = canFavorite ? EmojiPicker.isFavorite(emoji) : false;
	const reverseImageSearchUrl = emoji.id
		? AvatarUtils.getEmojiURL({id: emoji.id, animated: false})
		: (emoji.url ?? null);
	const handleToggleFavorite = useCallback(() => {
		EmojiPickerCommands.toggleFavorite(emoji);
	}, [emoji]);
	const handleCopyId = useCallback(() => {
		if (!emoji.id) return;
		TextCopyCommands.copy(i18n, emoji.id);
		onClose();
	}, [i18n, emoji.id, onClose]);
	const handleCopyUrl = useCallback(async () => {
		if (!reverseImageSearchUrl) return;
		await TextCopyCommands.copy(i18n, reverseImageSearchUrl, true);
		ToastCommands.createToast({
			type: 'success',
			children: t`Link copied to clipboard`,
		});
		onClose();
	}, [i18n, t, reverseImageSearchUrl, onClose]);
	const handleOpenInBrowser = useCallback(() => {
		if (!reverseImageSearchUrl) return;
		void openExternalUrl(reverseImageSearchUrl);
		onClose();
	}, [reverseImageSearchUrl, onClose]);
	return {
		canFavorite,
		isFavorite,
		reverseImageSearchUrl,
		handleToggleFavorite,
		handleCopyId,
		handleCopyUrl,
		handleOpenInBrowser,
	};
};
export const EmojiContextMenuItems = observer(({emoji, onClose}: EmojiContextMenuItemsProps) => {
	const {t} = useLingui();
	const {canFavorite, isFavorite, reverseImageSearchUrl, handleToggleFavorite, handleCopyId} = useEmojiHandlers(
		emoji,
		onClose,
	);
	const shouldShowPrimaryGroup = canFavorite || Boolean(emoji.id);
	return (
		<>
			{shouldShowPrimaryGroup && (
				<MenuGroup>
					{canFavorite && (
						<MenuItem
							icon={<StarIcon className={styles.iconSmall} weight={isFavorite ? 'fill' : 'bold'} />}
							onClick={handleToggleFavorite}
						>
							{isFavorite ? t`Unfavorite Emoji` : t`Favorite Emoji`}
						</MenuItem>
					)}
					{emoji.id && (
						<MenuItem icon={<ClipboardIcon className={styles.iconSmall} />} onClick={handleCopyId}>
							{t`Copy Emoji ID`}
						</MenuItem>
					)}
				</MenuGroup>
			)}
			{emoji.id && <CloneEmojiMenuItem emoji={emoji} onClose={onClose} />}
			{reverseImageSearchUrl && (
				<ReverseImageSearchMenuItems
					imageUrl={reverseImageSearchUrl}
					onClose={onClose}
					wrapInGroup
					includeCopyAndOpen
					copyLabel={t`Copy Emoji URL`}
					openLabel={t`Open Emoji in Browser`}
				/>
			)}
		</>
	);
});

EmojiContextMenuItems.displayName = 'EmojiContextMenuItems';
export const EmojiInlineMenuItems = observer(({emoji, onClose}: EmojiContextMenuItemsProps) => {
	const {t} = useLingui();
	const {
		canFavorite,
		isFavorite,
		reverseImageSearchUrl,
		handleToggleFavorite,
		handleCopyId,
		handleCopyUrl,
		handleOpenInBrowser,
	} = useEmojiHandlers(emoji, onClose);
	if (!canFavorite && !emoji.id && !reverseImageSearchUrl) {
		return null;
	}
	return (
		<MenuGroup>
			{canFavorite && (
				<MenuItem onClick={handleToggleFavorite}>{isFavorite ? t`Unfavorite Emoji` : t`Favorite Emoji`}</MenuItem>
			)}
			{reverseImageSearchUrl && <MenuItem onClick={handleCopyUrl}>{t`Copy Image Link`}</MenuItem>}
			{reverseImageSearchUrl && <MenuItem onClick={handleOpenInBrowser}>{t`Open Image Link`}</MenuItem>}
			<MenuItemSubmenu
				label={t`More Emoji Actions`}
				render={() => (
					<>
						<MenuGroup>
							{canFavorite && (
								<MenuItem
									icon={<StarIcon className={styles.iconSmall} weight={isFavorite ? 'fill' : 'bold'} />}
									onClick={handleToggleFavorite}
								>
									{isFavorite ? t`Unfavorite Emoji` : t`Favorite Emoji`}
								</MenuItem>
							)}
							{emoji.id && (
								<MenuItem icon={<ClipboardIcon className={styles.iconSmall} />} onClick={handleCopyId}>
									{t`Copy Emoji ID`}
								</MenuItem>
							)}
							{reverseImageSearchUrl && (
								<MenuItem icon={<LinkIcon className={styles.iconSmall} weight="bold" />} onClick={handleCopyUrl}>
									{t`Copy Emoji URL`}
								</MenuItem>
							)}
							{reverseImageSearchUrl && (
								<MenuItem icon={<ArrowSquareOutIcon className={styles.iconSmall} />} onClick={handleOpenInBrowser}>
									{t`Open Emoji in Browser`}
								</MenuItem>
							)}
						</MenuGroup>
						{emoji.id && <CloneEmojiMenuItem emoji={emoji} onClose={onClose} />}
						{reverseImageSearchUrl && (
							<ReverseImageSearchMenuItems imageUrl={reverseImageSearchUrl} onClose={onClose} wrapInGroup />
						)}
					</>
				)}
			/>
		</MenuGroup>
	);
});
EmojiInlineMenuItems.displayName = 'EmojiInlineMenuItems';
